import { useEffect, useMemo, useState } from 'react';
import { Icon } from '../components/Icon.jsx';
import { AlertBanner } from '../components/AlertBanner.jsx';

// Frontend demo data. Replace this adapter with the approved settings API
// when the backend endpoint and permission contract are available.
const DEMO_SETTINGS = [
  { id: 1, name: 'Bot configuration', role: 'Admin', status: 'Enabled', updated: 'Just now' },
  { id: 2, name: 'Conversation workspace', role: 'Agent', status: 'Enabled', updated: 'Today' },
  { id: 3, name: 'Analytics dashboard', role: 'Manager', status: 'Read only', updated: 'Yesterday' },
];

export function ResponsiveSettingsPanel({ mockErrorOn }) {
  const [role, setRole] = useState('All roles');
  const [filter, setFilter] = useState('');
  const [items, setItems] = useState([]);
  const [status, setStatus] = useState('loading'); // loading | ready | error
  const [lastUpdated, setLastUpdated] = useState(null);

  function refresh() {
    setStatus('loading');
    window.setTimeout(() => {
      if (mockErrorOn) {
        setStatus('error');
        return;
      }
      setItems(DEMO_SETTINGS);
      setLastUpdated(new Date().toLocaleTimeString());
      setStatus('ready');
    }, 350);
  }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { refresh(); }, [mockErrorOn]);

  const visible = useMemo(() => items.filter(item =>
    (role === 'All roles' || item.role === role) &&
    `${item.name} ${item.role} ${item.status}`.toLowerCase().includes(filter.toLowerCase())
  ), [items, role, filter]);

  return <section className="responsive-settings">
    <div className="panel-header">
      <h1>Responsive settings layout</h1>
      <p>Review settings access by role, filter the list, and refresh the latest settings data.</p>
    </div>
    <div className="card">
      <div className="responsive-toolbar">
        <div className="field responsive-filter">
          <label htmlFor="settings-filter">Filter settings</label>
          <input id="settings-filter" type="text" value={filter} onChange={e => setFilter(e.target.value)} placeholder="Search settings…" />
        </div>
        <div className="field responsive-role">
          <label htmlFor="settings-role">Role</label>
          <select id="settings-role" value={role} onChange={e => setRole(e.target.value)}>
            <option>All roles</option><option>Admin</option><option>Agent</option><option>Manager</option>
          </select>
        </div>
        <button type="button" className="btn-secondary refresh-button" onClick={refresh} disabled={status === 'loading'}>
          {status === 'loading' ? <span className="spinner" aria-hidden="true" /> : '↻'} Refresh
        </button>
      </div>
      {status === 'loading' && <div className="settings-feedback" role="status"><span className="spinner" /> Loading settings…</div>}
      {status === 'error' && <AlertBanner text="Settings could not be loaded. Check the connection and try again." onRetry={refresh} />}
      {status === 'ready' && visible.length === 0 && <div className="empty-state" role="status"><div className="empty-icon"><Icon.search s={22}/></div><h3>No settings found</h3><p>Try another search term or select a different role.</p><button className="btn-secondary" onClick={() => {setFilter('');setRole('All roles');}}>Clear filters</button></div>}
      {status === 'ready' && visible.length > 0 && <>
        <div className="settings-table-wrap">
          <table className="settings-table">
            <thead><tr><th>Setting</th><th>Role</th><th>Access</th><th>Last updated</th></tr></thead>
            <tbody>{visible.map(item => <tr key={item.id}><td data-label="Setting">{item.name}</td><td data-label="Role">{item.role}</td><td data-label="Access"><span className="settings-access">{item.status}</span></td><td data-label="Last updated">{item.updated}</td></tr>)}</tbody>
          </table>
        </div>
        <p className="settings-footnote" aria-live="polite">Showing {visible.length} of {items.length} settings{lastUpdated ? ` · Refreshed ${lastUpdated}` : ''}. Demo data; permissions are illustrative.</p>
      </>}
    </div>
  </section>;
}
